import React, { useState, useContext, useEffect, useMemo } from "react";
import DataTable from "core/ui/data-table";
import FormatDateTime from "core/utils/FormatDateTime";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from "reactstrap";
import SessionContext from "SessionContext";
import isEqual from "lodash.isequal";
import debounce from 'core/utils/debounce';
import ConfirmSuccess from "../core/ui/confirm-success";
import { LoadingDots } from "partials/progress-loader";
import { useHistory } from "react-router-dom";

function ResellerSettings() {

  const session = useContext(SessionContext);
  const history = useHistory();
  const [showCreateNewModal, setShowCreateNewModal] = useState(false);
  const [showMaxReachedNewModal, setShowMaxReachedNewModal] = useState(false);
  const [isNameValid, setIsNameValid] = useState(false);
  const [isNameNotValid, setIsNameNotValid] = useState(false);
  const [isNotReadyToSubmit, setNotReadyToSubmit] = useState(true);
  const [parentCompany, setParentCompany] = useState({});
  const [newName, setNewName] = useState("");
  const [subCompanyCount, setSubCompanyCount] = useState(0);
  const [maxSubCompaniesAllowed, setMaxSubCompaniesAllowed] = useState(10);
  const [tableKey, setTableKey] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);

  useEffect(() => {
    if(!isEqual(parentCompany, session.appState.activeCompany)) {
      setParentCompany(session.appState.activeCompany);
      setMaxSubCompaniesAllowed(session.appState.activeCompany.approved_sub_company_count);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.appState.activeCompany])

  const table_columns = [
    { header: 'Name', key: 'name'},
    { header: 'Date Created', key: 'created_date', render: (value) => <FormatDateTime date={value.created_date} />},
  ];

  const onClickNew = (ev) => {
    ev.stopPropagation();
    if(subCompanyCount < maxSubCompaniesAllowed) {
      setShowCreateNewModal(true);
    } else {
      setShowMaxReachedNewModal(true);
    }
  }

  const onResetAll = () => {
    // reset all fields
    setNotReadyToSubmit(true);
    setIsNameNotValid(false);
    setIsNameValid(false);
    setNewName("");
  }

  const toggleShowCreateNewModal = () => {
    onResetAll();
    setShowCreateNewModal(!showCreateNewModal);
  }

  const handleCreateNew = () => {
    setIsWaiting(true);
    session
      .command(
        "companies/create_sub_company",
        { "name": newName },
        {},
        { defaultErrorHandling: false }
      )
      .then(() => {
        setIsWaiting(false);
        toggleShowCreateNewModal();
        setShowSuccess(true);
        setTableKey(!tableKey);
        setTimeout(() => {
          setShowSuccess(false);
        }, 2000);
      })
      .catch((error) => {
        setIsWaiting(false);
        toggleShowCreateNewModal();
        console.log("Failed to add new sub-company: ", error)
      })
  }

  const runCheck = (inputValue) => {
    let _val = inputValue.trim();
    if(!_val.length) {
      setIsNameValid(false);
      setIsNameNotValid(false);
      setNotReadyToSubmit(true);
      return;
    }

    session
      .query("companies/is_valid_sub_company_create", { name: _val })
      .then((response) => {
        if(response.success) {
          setIsNameValid(true);
          setIsNameNotValid(false);
          setNotReadyToSubmit(false);
        } else {
          setIsNameValid(false);
          setIsNameNotValid(true);
          setNotReadyToSubmit(true);
        } 
      })
      .catch((error) => {
        console.log("Failed checking new name: ", error)
      });
  }

  const debouncedHandleChange = useMemo(
    () =>
      debounce((inputValue) => {
        runCheck(inputValue);
      }, 300),
    // eslint-disable-next-line
    []
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setNewName(newValue);
    debouncedHandleChange(newValue);
  };

  const getRowCount = (val) => {
    setSubCompanyCount(val);
  }

  function onClickBack(ev) {
    ev.stopPropagation();
    history.push("/company-settings");
  }

  return (
    <div className="page admin-page">
      <div className="list-header sticky-page-header">
        <button className="new-button" onClick={onClickBack}>
          <i className="bi bi-chevron-left"></i> Settings
        </button>
        <h1>Reseller Settings</h1>
        <i className="spacer" />
        <Button className='new-button' onClick={onClickNew}>New Sub-Company</Button>
      </div>
      <div className="companies">
        <DataTable query="companies/list_for_reseller" 
          cols={table_columns}
          empty="No Sub-Companies Found" 
          getRowCount={getRowCount}
          key={tableKey}
        />
      </div>
      <Modal centered isOpen={showCreateNewModal} backdrop>
        <ModalHeader>
           Create Sub-Company
        </ModalHeader>
        <ModalBody>
          {isWaiting ? <div className="my-3">
            <LoadingDots message="Saving Sub-Company, this may take a few moments." />
            </div> : <div>
            <div>Parent Company: <strong>{parentCompany.name}</strong></div><br />
            <div>New Sub-Company Name:</div>
            <div className="my-3">
              <input
                type="text"
                className="form-control w-100" 
                value={newName}
                onChange={handleInputChange}
                placeholder="Enter a sub-company name..."
              />
            </div>
            <div>
              {isNameValid && <div className="mt-2 ms-2"><i className="bi bi-check-lg text-success"/>Name is available</div>}
              {isNameNotValid && <div className="mt-2 ms-2"><i className="bi bi-exclamation-triangle text-danger"/> Name already in use</div>}
              {!isNameNotValid && !isNameValid ? <div className="mt-2">&nbsp;</div> : ""}
            </div>
          </div>}  
        </ModalBody>
        <ModalFooter>
          {isWaiting ? <div className="my-2">&nbsp;</div> : <div>
            <Button onClick={toggleShowCreateNewModal}>
              Cancel
            </Button>
            <Button className={" promote ms-2"} onClick={handleCreateNew} disabled={isNotReadyToSubmit}>Save</Button>
          </div> }
        </ModalFooter>
      </Modal>
      <Modal centered isOpen={showMaxReachedNewModal} backdrop>
        <ModalHeader toggle={() => setShowMaxReachedNewModal(false)}>
          Maximum Allowed Limit Reached
        </ModalHeader>
        <ModalBody>
          <Alert color="warning" className="d-flex">
            <i className="bi bi-exclamation-octagon"></i>
            <div>You have reached the maximum allowed limit of sub-companies. Please contact your Customer Success representative to review options.</div>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button className={" promote"} onClick={() => setShowMaxReachedNewModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>
      <ConfirmSuccess show={showSuccess} label="Sub-Company Created Successfully." />
    </div>
  )
}

export default ResellerSettings;
